import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { normalizeCompanyTier, type CompanyTier } from '@/lib/tier-permissions';

export function useCompanyTier() {
  const { companyId, loading: authLoading } = useAuth();
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const [tier, setTier] = useState<CompanyTier>('free');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || adminLoading) return;

    if (!companyId) {
      setTier('free');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTier = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('tier')
          .eq('id', companyId)
          .single();

        if (cancelled) return;

        if (error) {
          console.error('Error fetching company tier:', error);
          setTier('free');
        } else {
          setTier(normalizeCompanyTier(data?.tier));
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error in useCompanyTier:', err);
        setTier('free');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchTier();

    return () => { cancelled = true; };
  }, [companyId, authLoading, adminLoading]);

  return {
    tier,
    isPlatformAdmin,
    loading: loading || authLoading || adminLoading,
  };
}
